import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, EnvelopeIcon, PencilIcon, BookOpenIcon, AcademicCapIcon, VideoCameraIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../services/api'
import { sanitizeHtml } from '../lib/sanitizeHtml'
import { useState, useEffect, useRef } from 'react'
import EmailModal from './EmailModal'
import { LikeButton } from './LikeButton'
import type { Resource, DemoVideo } from '../types'

export default function TemplateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState<'image' | 'gif' | 'video'>('image')
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [activeTab, setActiveTab] = useState<'media' | 'resources' | 'feedback'>('media')
  const [feedbackClient, setFeedbackClient] = useState('Gmail')
  const [feedbackEmail, setFeedbackEmail] = useState(() => {
    return localStorage.getItem('showcase_email') || ''
  })
  const [feedbackIssueType, setFeedbackIssueType] = useState('Display issues')
  const [feedbackComment, setFeedbackComment] = useState('')
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const autoPlayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  
  // Check if user is admin
  const isAdmin = localStorage.getItem('admin_token') !== null
  
  const { data: template, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => apiClient.getTemplate(Number(id)),
    enabled: !!id
  })

  // Log view when template is loaded
  useEffect(() => {
    console.log('TemplateDetail useEffect triggered:', { template: !!template, id })
    if (template && id) {
      console.log('Logging view for template:', template.name, 'ID:', id)
      // Log view when user opens template details page
      apiClient.createView(Number(id))
        .then(response => {
          console.log('Template view logged successfully:', response)
        })
        .catch(error => {
          console.error('Failed to log template view:', error)
        })
    }
  }, [template, id])

  // Fetch resources for this template
  const { data: resources = [] } = useQuery<Resource[]>({
    queryKey: ['resources', template?.esputnik_message_id],
    queryFn: () => apiClient.getResourcesByTemplate(template!.esputnik_message_id),
    enabled: !!template?.esputnik_message_id
  })
  const hasResources = resources.length > 0

  // Fetch demo videos for this template
  const { data: demoVideos = [] } = useQuery<DemoVideo[]>({
    queryKey: ['demoVideos', template?.esputnik_message_id],
    queryFn: () => apiClient.getDemoVideosByTemplate(template!.esputnik_message_id),
    enabled: !!template?.esputnik_message_id
  })

  // Helper function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    // Handle embed format: https://www.youtube.com/embed/VIDEO_ID
    if (url.includes('/embed/')) {
      return url.split('/embed/')[1].split('?')[0]
    }
    // Handle watch format: https://www.youtube.com/watch?v=VIDEO_ID
    if (url.includes('watch?v=')) {
      return url.split('watch?v=')[1].split('&')[0]
    }
    // Handle short format: https://youtu.be/VIDEO_ID
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0]
    }
    return null
  }

  // Get YouTube thumbnail URL
  const getYouTubeThumbnail = (url: string): string => {
    const videoId = getYouTubeVideoId(url)
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    }
    return ''
  }

  // Default media: GIF > real demo videos > preview image > legacy video_url (no demos yet)
  useEffect(() => {
    if (!template) return

    if (template.gif_url) {
      setSelectedMedia('gif')
    } else if (demoVideos.length > 0) {
      setSelectedMedia('video')
    } else if (template.preview_image) {
      setSelectedMedia('image')
    } else if (template.video_url) {
      setSelectedMedia('video')
    }
  }, [template, demoVideos.length])

  // Auto-play carousel effect
  useEffect(() => {
    if (isAutoPlay && demoVideos.length > 1) {
      autoPlayIntervalRef.current = setInterval(() => {
        setCurrentVideoIndex((prev) => (prev === demoVideos.length - 1 ? 0 : prev + 1))
      }, 5000) // Change video every 5 seconds
    } else {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
        autoPlayIntervalRef.current = null
      }
    }

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
    }
  }, [isAutoPlay, demoVideos.length])

  // Reset auto-play when video changes manually
  const handleVideoChange = (index: number) => {
    setCurrentVideoIndex(index)
    setIsAutoPlay(false)
  }

  const goToPreviousVideo = () => {
    setCurrentVideoIndex((prev) => (prev === 0 ? demoVideos.length - 1 : prev - 1))
    setIsAutoPlay(false)
  }

  const goToNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev === demoVideos.length - 1 ? 0 : prev + 1))
    setIsAutoPlay(false)
  }

  const handleDeleteResource = async (resourceId: number) => {
    if (!confirm('Are you sure you want to delete this resource?')) {
      return
    }
    
    try {
      await apiClient.deleteResource(resourceId)
      // Refresh resources list
      queryClient.invalidateQueries({ queryKey: ['resources', template?.esputnik_message_id] })
    } catch (error) {
      console.error('Error deleting resource:', error)
      alert('Failed to delete resource')
    }
  }

  const handleDeleteDemoVideo = async (videoId: number) => {
    if (!confirm('Are you sure you want to delete this demo video?')) {
      return
    }
    
    try {
      await apiClient.deleteDemoVideo(videoId)
      // Refresh demo videos list
      queryClient.invalidateQueries({ queryKey: ['demoVideos', template?.esputnik_message_id] })
      // Reset to first video if current was deleted
      if (currentVideoIndex >= demoVideos.length - 1) {
        setCurrentVideoIndex(0)
      }
    } catch (error) {
      console.error('Error deleting demo video:', error)
      alert('Failed to delete demo video')
    }
  }

  const handleSubmitFeedback = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!template) return

    setIsSubmittingFeedback(true)
    setFeedbackMessage('')
    try {
      const result = await apiClient.submitFeedback({
        template_id: template.id,
        email: feedbackEmail.trim() || undefined,
        email_client: feedbackClient,
        issue_type: feedbackIssueType,
        comment: feedbackComment.trim() || undefined,
        page_url: window.location.href
      })
      if (result.success) {
        setFeedbackMessage('Thanks! Your feedback has been submitted.')
        setFeedbackComment('')
        if (feedbackEmail.trim()) {
          localStorage.setItem('showcase_email', feedbackEmail.trim())
        }
      } else {
        setFeedbackMessage(result.message || 'Failed to submit feedback.')
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      setFeedbackMessage('Failed to submit feedback.')
    } finally {
      setIsSubmittingFeedback(false)
    }
  }

  const getResourceIcon = (type: string) => {
    if (type.toLowerCase().includes('video') || type.toLowerCase().includes('webinar')) {
      return <VideoCameraIcon className="w-5 h-5" />
    }
    if (type.toLowerCase().includes('course') || type.toLowerCase().includes('guide')) {
      return <AcademicCapIcon className="w-5 h-5" />
    }
    return <BookOpenIcon className="w-5 h-5" />
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Email not found</p>
        <Link
          to="/"
          className="text-primary-600 hover:text-primary-700 mt-2 inline-block"
        >
          Return to catalog
        </Link>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Return to catalog</span>
        </Link>

        {/* Template Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{template.name}</h1>
              {template.category && (
                <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                  {template.category}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              {template.web_version_url && (
                <a
                  href={template.web_version_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 border border-primary-500 text-primary-500 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  <span>View Web Version</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
              
              {isAdmin && (
                <button
                  onClick={() => navigate(`/admin?edit=${template.id}`)}
                  className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <PencilIcon className="w-5 h-5" />
                  <span>Edit</span>
                </button>
              )}
              
              <button
                onClick={() => setShowEmailModal(true)}
                className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <EnvelopeIcon className="w-5 h-5" />
                <span>Send to me</span>
              </button>
            </div>
          </div>

          {template.description?.trim() && (
            <div
              className="text-gray-600 text-lg leading-relaxed break-words [&_a]:text-primary-500 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(template.description),
              }}
            />
          )}
        </div>

        {/* Unified Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="px-6 py-3 flex items-center gap-2 flex-wrap">
            {template.preview_image && (
              <button
                onClick={() => { setActiveTab('media'); setSelectedMedia('image') }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'media' && selectedMedia === 'image'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Preview Image
              </button>
            )}
            {template.gif_url && (
              <button
                onClick={() => { setActiveTab('media'); setSelectedMedia('gif') }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'media' && selectedMedia === 'gif'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                GIF Animation
              </button>
            )}
            {demoVideos.length > 0 && (
              <button
                onClick={() => { setActiveTab('media'); setSelectedMedia('video') }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'media' && selectedMedia === 'video'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Demo Video ({demoVideos.length})
              </button>
            )}
            {hasResources && (
              <button
                onClick={() => setActiveTab('resources')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'resources'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Resources ({resources.length})
              </button>
            )}
            <button
              onClick={() => setActiveTab('feedback')}
              className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'feedback'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Feedback
            </button>
          </div>

          {activeTab === 'media' && (
          <div className="p-6 pt-0">
          {/* Media Display */}
          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
            {selectedMedia === 'image' && template.preview_image && (
              <img
                src={template.preview_image}
                alt={template.name}
                className="w-full h-full object-contain"
              />
            )}
            {selectedMedia === 'gif' && template.gif_url && (
              <img
                src={template.gif_url}
                alt={`${template.name} animation`}
                className="w-full h-full object-contain"
              />
            )}
            {selectedMedia === 'video' && demoVideos.length > 0 && (
                  <div className="w-full space-y-4">
                    {/* Main Video Player with Preview */}
                    <div className="relative">
                      {/* Previous Video Preview */}
                      {demoVideos.length > 1 && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 hidden md:block">
                          <button
                            onClick={goToPreviousVideo}
                            className="group relative w-24 h-16 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all hover:scale-110"
                            aria-label="Previous video"
                          >
                            <div className="absolute inset-0 bg-black/50 group-hover:bg-black/70 transition-colors flex items-center justify-center">
                              <ChevronLeftIcon className="w-8 h-8 text-white" />
                            </div>
                            <img
                              src={getYouTubeThumbnail(demoVideos[currentVideoIndex === 0 ? demoVideos.length - 1 : currentVideoIndex - 1].youtube_link)}
                              alt="Previous video"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                              <p className="text-xs text-white truncate">
                                {demoVideos[currentVideoIndex === 0 ? demoVideos.length - 1 : currentVideoIndex - 1].email_client}
                              </p>
                            </div>
                          </button>
                        </div>
                      )}

                      {/* Main Video */}
                      <div className="w-full aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
                        <iframe
                          src={demoVideos[currentVideoIndex].youtube_link}
                          title={demoVideos[currentVideoIndex].name}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      {/* Next Video Preview */}
                      {demoVideos.length > 1 && (
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 hidden md:block">
                          <button
                            onClick={goToNextVideo}
                            className="group relative w-24 h-16 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all hover:scale-110"
                            aria-label="Next video"
                          >
                            <div className="absolute inset-0 bg-black/50 group-hover:bg-black/70 transition-colors flex items-center justify-center">
                              <ChevronRightIcon className="w-8 h-8 text-white" />
                            </div>
                            <img
                              src={getYouTubeThumbnail(demoVideos[currentVideoIndex === demoVideos.length - 1 ? 0 : currentVideoIndex + 1].youtube_link)}
                              alt="Next video"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                              <p className="text-xs text-white truncate">
                                {demoVideos[currentVideoIndex === demoVideos.length - 1 ? 0 : currentVideoIndex + 1].email_client}
                              </p>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Video Info */}
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {demoVideos[currentVideoIndex].name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {demoVideos[currentVideoIndex].email_client}
                      </p>
                    </div>
                    
                    {/* Carousel Controls */}
                    {demoVideos.length > 1 && (
                      <div className="space-y-4">
                        {/* Auto-play Toggle */}
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => setIsAutoPlay(!isAutoPlay)}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                            aria-label={isAutoPlay ? 'Pause auto-play' : 'Start auto-play'}
                          >
                            {isAutoPlay ? (
                              <>
                                <PauseIcon className="w-5 h-5 text-gray-700" />
                                <span className="text-sm text-gray-700">Pause Auto-play</span>
                              </>
                            ) : (
                              <>
                                <PlayIcon className="w-5 h-5 text-gray-700" />
                                <span className="text-sm text-gray-700">Start Auto-play</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Navigation Controls */}
                        <div className="flex items-center justify-center space-x-4">
                          <button
                            onClick={goToPreviousVideo}
                            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors md:hidden"
                            aria-label="Previous video"
                          >
                            <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
                          </button>
                          
                          {/* Video Thumbnails */}
                          <div className="flex space-x-2 overflow-x-auto pb-2 max-w-full">
                            {demoVideos.map((video, index) => {
                              const thumbnail = getYouTubeThumbnail(video.youtube_link)
                              return (
                                <div
                                  key={video.id}
                                  className="flex-shrink-0 relative group"
                                >
                                  <button
                                    onClick={() => handleVideoChange(index)}
                                    className={`relative w-20 h-12 rounded-lg overflow-hidden transition-all ${
                                      index === currentVideoIndex
                                        ? 'ring-2 ring-primary-500 scale-110'
                                        : 'opacity-60 hover:opacity-100 hover:scale-105'
                                    }`}
                                    aria-label={`Go to ${video.name}`}
                                  >
                                    <img
                                      src={thumbnail}
                                      alt={video.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none'
                                        const parent = (e.target as HTMLImageElement).parentElement
                                        if (parent) {
                                          parent.className += ' bg-gray-300'
                                        }
                                      }}
                                    />
                                    {index === currentVideoIndex && (
                                      <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                                        <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-0.5">
                                      <p className="text-[8px] text-white truncate leading-tight">
                                        {video.email_client}
                                      </p>
                                    </div>
                                  </button>
                                  {isAdmin && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteDemoVideo(video.id)
                                      }}
                                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                                      title="Delete video"
                                      aria-label="Delete video"
                                    >
                                      <TrashIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                          
                          <button
                            onClick={goToNextVideo}
                            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors md:hidden"
                            aria-label="Next video"
                          >
                            <ChevronRightIcon className="w-6 h-6 text-gray-700" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
            )}
            {selectedMedia === 'video' && demoVideos.length === 0 && template.preview_image && (
              <img
                src={template.preview_image}
                alt={template.name}
                className="w-full h-full object-contain"
              />
            )}
            {selectedMedia === 'video' && demoVideos.length === 0 && !template.preview_image && template.video_url && (
              <div className="w-full h-full flex items-center justify-center p-8">
                <div className="text-center max-w-2xl">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Demo Video Coming Soon</h3>
                  <p className="text-gray-600">
                    We're preparing demo videos showing how this email looks across different email clients (Gmail, Apple Mail, Outlook) on both mobile and desktop devices.
                  </p>
                </div>
              </div>
            )}
            {!template.preview_image && !template.gif_url && !template.video_url && demoVideos.length === 0 && (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-gray-500">No media available</span>
              </div>
            )}
          </div>

        {/* Fallback Stack and Compatibility - Side by Side on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Fallback Stack Section */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Fallback Stack</h2>
            {template.features && template.features.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {template.features.map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No fallback stack specified</p>
            )}
          </div>

          {/* Compatibility Section */}
          {template.compatibility && Object.keys(template.compatibility).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Compatibility</h2>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(template.compatibility).map(([client, supported]) => (
                  <div
                    key={client}
                    className={`flex items-center justify-between px-3 py-1.5 rounded border text-xs ${
                      supported
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                  >
                    <span className="capitalize">{client}</span>
                    <span>{supported ? 'Yes' : 'No'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Statistics</h2>
            <LikeButton 
              templateId={template.id} 
              initialLikeCount={template.like_count}
              size="lg"
            />
          </div>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary-600">{template.view_count}</div>
              <div className="text-sm text-gray-600">Views</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{template.send_count}</div>
              <div className="text-sm text-gray-600">Sends</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{template.like_count}</div>
              <div className="text-sm text-gray-600">Likes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{template.popularity_score}</div>
              <div className="text-sm text-gray-600">Popularity</div>
            </div>
          </div>
        </div>
          </div>
        )}

        {/* Feedback Section */}
        {activeTab === 'feedback' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Report an issue</h2>
            <p className="text-sm text-gray-500">
              Tell us what did not work and we will follow up when it is fixed.
            </p>
          </div>
          <form onSubmit={handleSubmitFeedback} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 text-left">
                Your email
              </label>
              <input
                type="email"
                value={feedbackEmail}
                onChange={(e) => setFeedbackEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <fieldset>
                <legend className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 text-left">
                  Email client
                </legend>
                <div className="space-y-2 text-sm text-gray-700">
                  {['Apple Mail', 'Gmail', 'Outlook', 'Other'].map((client) => (
                    <label key={client} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="feedback-email-client"
                        value={client}
                        checked={feedbackClient === client}
                        onChange={(e) => setFeedbackClient(e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        required
                      />
                      <span>{client}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 text-left">
                  Type of problem
                </legend>
                <div className="space-y-2 text-sm text-gray-700">
                  {[
                    'Display issues',
                    'Marked as spam and images did not load',
                    'Not working at all'
                  ].map((issue) => (
                    <label key={issue} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="feedback-issue-type"
                        value={issue}
                        checked={feedbackIssueType === issue}
                        onChange={(e) => setFeedbackIssueType(e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        required
                      />
                      <span>{issue}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 text-left">
                Details
              </label>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={4}
                placeholder="What exactly is not working?"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              {feedbackMessage && (
                <div className={`px-3 py-2 rounded-md text-sm ${
                  feedbackMessage.includes('Thanks')
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}>
                  {feedbackMessage}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmittingFeedback}
                className="ml-auto inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmittingFeedback ? 'Submitting...' : 'Submit feedback'}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Resources Section */}
        {activeTab === 'resources' && resources.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:shadow-md transition-all h-full relative group"
                >
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        handleDeleteResource(resource.id)
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-100 text-red-600 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-200 transition-all"
                      title="Delete resource"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                  <div className="flex-shrink-0 text-primary-600 mt-1">
                    {getResourceIcon(resource.type)}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center space-x-2 mb-2 flex-shrink-0">
                      <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                        {resource.type}
                      </span>
                    </div>
                    {resource.description && (
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gray-700 leading-relaxed flex-1 hover:text-primary-600 transition-colors"
                      >
                        {resource.description}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>

      </div>

      {showEmailModal && (
        <EmailModal
          templateIds={[template.id]}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </>
  )
}
